import { createApi } from '@reduxjs/toolkit/query/react';

import type { IClient, ICanDelete } from 'src/types/clients';
import { toast } from 'src/components/snackbar';

import { authQuery } from './baseQuery';
import type { MakeOptional, PaginatedResponse } from 'src/types/common';
import type { IPaginationState } from 'src/utils/server-side-pagination';
import { convertObjToUrl } from 'src/utils/server-side-pagination';

type ClientsResponse = MakeOptional<PaginatedResponse<IClient>, 'limit' | 'page' | 'totalPages'>;
type IBankAccount = {
  id: string;
  clientId: string;
  name: string;
  type: 'client' | 'service';
  currency: string;
  iban?: string;
  swiftBic?: string;
  bankName?: string;
  bgBankAccount?: string;
  readableId: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};
export const clientsAPISlice = createApi({
  reducerPath: 'presentation-service-clients-api',
  tagTypes: ['Clients'],
  baseQuery: authQuery,
  endpoints: (builder) => ({
    getClients: builder.query<ClientsResponse, void | string[]>({
      query: (attributes?) =>
        `/clients${attributes && attributes.length > 0 ? `?attributes=${attributes.join(',')}` : ''}`,
      providesTags: ['Clients'],
    }),

    getMyClient: builder.query<Pick<IClient, 'id' | 'name' | 'logo_uri'>, void>({
      query: () => '/clients/me',
    }),

    getClientsPaginated: builder.query<
      PaginatedResponse<IClient>,
      { state: IPaginationState; limit: number }
    >({
      query: ({ state, limit }) => `/clients?limit=${limit}${convertObjToUrl(state)}`,
      providesTags: ['Clients'],
    }),

    getClient: builder.query<IClient, string>({
      query: (clientId) => `/clients/${clientId}`,
      providesTags: ['Clients'],
    }),

    getOIDCClient: builder.query<{ clientId: string; clientSecret: string }, string>({
      query: (clientId) => `/clients/${clientId}/oidc`,
      providesTags: ['Clients'],
    }),

    refreshOIDCSecret: builder.mutation<{ clientId: string; clientSecret: string }, string>({
      query: (clientId) => ({
        url: `/clients/${clientId}/oidc/refresh-secret`,
        method: 'POST',
      }),
    }),

    addClient: builder.mutation<
      { id: string; slug: string; readableId: string; name: string },
      Partial<IClient>
    >({
      query: (requestData: Partial<IClient>) => ({
        url: `/clients/create`,
        method: 'POST',
        body: requestData,
      }),
      // invalidatesTags: ["Clients"],
      async onQueryStarted(requestData, { dispatch, queryFulfilled }) {
        try {
          const queryResponse = await queryFulfilled;

          dispatch(
            clientsAPISlice.util.updateQueryData('getClients', undefined, (allClientsDraft) => {
              const newClient = {
                ...requestData,
                ...queryResponse.data,
              } as IClient;

              allClientsDraft.data.push(newClient);
            })
          );
        } catch (e) {
          const err = e as { error?: APIResponseError } | APIResponseError;
          const message = 'error' in err ? err.error?.data.message : err;
          toast.error(`Error creating client: ${message}`, {
            position: 'bottom-right',
          });

          console.log('Error creating client!', e);
        }
      },
    }),

    updateClient: builder.mutation<string, { id: string; body: Partial<IClient> }>({
      query: ({ id, body }) => ({
        url: `/clients/update/${id}`,
        method: 'POST',
        body,
      }),
      // invalidatesTags: ["Clients"],
      async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            clientsAPISlice.util.updateQueryData('getClients', undefined, (allClientsDraft) => {
              const clientIndex = allClientsDraft.data.findIndex((item) => item.id === id);

              if (clientIndex !== -1) {
                allClientsDraft.data[clientIndex] = {
                  ...allClientsDraft.data[clientIndex],
                  ...body,
                };
              }
            })
          );
        } catch {
          console.log('Error updating client!');
        }
      },
    }),

    deleteClient: builder.mutation<ICanDelete, string>({
      query: (id) => ({
        url: `/clients/delete/${id}`,
        method: 'POST',
      }),
      // invalidatesTags: ["Clients"],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            clientsAPISlice.util.updateQueryData('getClients', undefined, (allClientsDraft) => {
              const clientIndex = allClientsDraft.data.findIndex((item) => item.id === id);

              if (clientIndex !== -1) {
                allClientsDraft.data.splice(clientIndex, 1);
              }
            })
          );
        } catch {
          console.log('Error updating client!');
        }
      },
    }),

    getBankAccountsByClientId: builder.query<IBankAccount[], { clientId: string }>({
      query: ({ clientId }) => ({
        url: `/clients/${clientId}/bank-accounts`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetClientsQuery,
  useAddClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useGetClientQuery,
  useGetMyClientQuery,
  useGetOIDCClientQuery,
  useRefreshOIDCSecretMutation,
  useGetClientsPaginatedQuery,
  useGetBankAccountsByClientIdQuery,
} = clientsAPISlice;
